import { IeLynxObject } from "../../common/models/eLynxObject";
import { RestError } from "../../services/rest-service/RestService";
import { useMutation, useQueryClient } from "react-query";
import { IRunTicketModel } from "../../services/run-tickets-data-service/Models";
import RunTicketsDataService from "../../services/run-tickets-data-service/RunTicketsDataService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";

export type UseSaveRunTicketParameters = {
    customerID: number,
    groupID: number,
    contextObject: IeLynxObject,
    timestamp: Date,
} & BaseUseMutationParameters<void, IRunTicketModel>;

export function useSaveRunTicket({
    customerID,
    groupID,
    contextObject,
    timestamp,
    offline,
    onError,
    onSuccess
}: UseSaveRunTicketParameters) {
    const queryClient = useQueryClient();

    return useMutation<void, RestError, IRunTicketModel, MutationContextType>(
        (runTicket) => {
            return RunTicketsDataService.saveRunTicket(
                customerID,
                groupID,
                runTicket,
                offline ?? false
            );
        },
        {
            onMutate: async (runTicket: IRunTicketModel) => {

                await queryClient.cancelQueries(keys.all);

                const previousRunTickets = queryClient.getQueryData<Array<IRunTicketModel>>(
                    keys.list(
                        customerID,
                        groupID,
                        contextObject,
                        offline ?? false,
                        timestamp
                    )
                );

                queryClient.setQueryData<Array<IRunTicketModel>>(
                    keys.list(
                        customerID,
                        groupID,
                        contextObject,
                        offline ?? false,
                        timestamp
                    ),
                    (old?: Array<IRunTicketModel>) => {

                        const existingRunTicket = old?.find(rt => rt.id === runTicket.id);

                        return existingRunTicket
                            ? old?.map(rt => (rt.id === runTicket.id ? runTicket : rt)) ?? []
                            : [...(old ?? []), runTicket];
                    }
                );

                return {
                    previousRunTickets
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _runTicket, context) => {

                if (error && context?.previousRunTickets) {
                    queryClient.setQueryData<Array<IRunTicketModel>>(
                        keys.list(
                            customerID,
                            groupID,
                            contextObject,
                            offline ?? false,
                            timestamp
                        ),
                        context?.previousRunTickets
                    );
                }

                queryClient.invalidateQueries(keys.lists());
            }
        }
    );
}
